import type {
  LayoutPoint,
  ViewerEntityId,
  ViewerPosition,
} from '../contracts/models';

export interface EditableLayoutPolygon<
  TId extends ViewerEntityId = ViewerEntityId,
> {
  id: TId;
  points: readonly (readonly number[])[];
}

export interface FindPolygonSnapTargetOptions<
  TId extends ViewerEntityId = ViewerEntityId,
> {
  cursor: ViewerPosition;
  polygons: readonly EditableLayoutPolygon<TId>[];
  pendingPoints?: ReadonlyMap<TId, readonly (readonly number[])[]>;
  excludePolygonId?: TId;
  threshold: number;
  roundEdgeProjection?: boolean;
}

const asLayoutPoint = (
  point: readonly number[],
): LayoutPoint | null => {
  const x = Number(point[0]);
  const y = Number(point[1]);
  return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
};

export const getPolygonSnapThreshold = (scale: number): number => {
  const safeScale =
    Number.isFinite(scale) && scale > 0 ? scale : 1;
  return Math.max(4, Math.min(10, 8 / safeScale));
};

export const findPolygonSnapTarget = <
  TId extends ViewerEntityId = ViewerEntityId,
>({
  cursor,
  polygons,
  pendingPoints,
  excludePolygonId,
  threshold,
  roundEdgeProjection = true,
}: FindPolygonSnapTargetOptions<TId>): ViewerPosition | null => {
  let bestTarget: ViewerPosition | null = null;
  let bestDistance =
    Number.isFinite(threshold) && threshold > 0 ? threshold : 0;

  polygons.forEach((polygon) => {
    if (
      excludePolygonId !== undefined &&
      String(polygon.id) === String(excludePolygonId)
    ) {
      return;
    }

    const sourcePoints =
      pendingPoints?.get(polygon.id) ?? polygon.points;
    const points = sourcePoints
      .map(asLayoutPoint)
      .filter((point): point is LayoutPoint => point !== null);
    if (points.length < 2) return;

    points.forEach(([x, y]) => {
      const distance = Math.hypot(cursor.x - x, cursor.y - y);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestTarget = { x, y };
      }
    });

    points.forEach(([startX, startY], index) => {
      const [endX, endY] = points[(index + 1) % points.length];
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const lengthSquared = deltaX ** 2 + deltaY ** 2;
      if (lengthSquared === 0) return;

      const projectionRatio = Math.max(
        0,
        Math.min(
          1,
          ((cursor.x - startX) * deltaX +
            (cursor.y - startY) * deltaY) /
            lengthSquared,
        ),
      );
      const projectedX = startX + projectionRatio * deltaX;
      const projectedY = startY + projectionRatio * deltaY;
      const distance = Math.hypot(
        cursor.x - projectedX,
        cursor.y - projectedY,
      );

      if (distance < bestDistance) {
        bestDistance = distance;
        bestTarget = {
          x: roundEdgeProjection
            ? Math.round(projectedX)
            : projectedX,
          y: roundEdgeProjection
            ? Math.round(projectedY)
            : projectedY,
        };
      }
    });
  });

  return bestTarget;
};
